import React, { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface CardProps {
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
  action?: ReactNode;
  footer?: ReactNode;
  borderColor?: string;
  hoverable?: boolean;
  noPadding?: boolean;
  onClick?: () => void;
}

/**
 * Card component
 * 
 * A versatile card component that can be used for dashboard cards, list items, etc.
 */
export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  title,
  subtitle,
  icon,
  action,
  footer,
  borderColor,
  hoverable = false,
  noPadding = false,
  onClick,
}) => {
  const Component = onClick ? 'div' : 'div';
  const props = onClick ? { onClick, role: 'button' as const, tabIndex: 0, onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } } : {};

  return (
    <Component
      className={cn(
        'rounded-lg border border-border bg-card',
        borderColor && `border-t-4 border-t-[${borderColor}]`,
        hoverable && 'transition-colors duration-200 hover:border-border/80',
        onClick && 'cursor-pointer',
        className
      )}
      {...props}
    >
      {(title || icon || action) && (
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center space-x-3">
            {icon && <div className="text-muted-foreground">{icon}</div>}
            <div>
              {title && <h3 className="font-medium text-card-foreground">{title}</h3>}
              {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          {action && <div>{action}</div>}
        </div>
      )}

      <div className={noPadding ? '' : 'p-4'}>
        {children}
      </div>

      {footer && (
        <div className="border-t border-border bg-muted/50 px-4 py-3 rounded-b-lg">
          {footer}
        </div>
      )}
    </Component>
  );
};

/**
 * CardHeader component for more complex header layouts
 */
export const CardHeader: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <div className={cn('border-b border-border px-4 py-3', className)}>
      {children}
    </div>
  );
};

/**
 * CardContent component
 */
export const CardContent: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return <div className={cn('p-4', className)}>{children}</div>;
};

/**
 * CardTitle component
 */
export const CardTitle: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <h3 className={cn('font-semibold text-lg font-display text-card-foreground', className)}>
      {children}
    </h3>
  );
};

/**
 * CardDescription component
 */
export const CardDescription: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <p className={cn('text-sm text-muted-foreground', className)}>
      {children}
    </p>
  );
};

/**
 * CardFooter component
 */
export const CardFooter: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className = '' }) => {
  return (
    <div className={cn('border-t border-border bg-muted/50 px-4 py-3', className)}>
      {children}
    </div>
  );
};

export default Card; 